import { useState, useEffect } from 'react';
import { Leaf, Menu, X, Phone } from 'lucide-react';

const NAV_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/wellness-suite', label: 'Wellness Suite' },
  { href: '#team', label: 'Our Team' },
  { href: '#contact', label: 'Contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const handleClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      if (path !== '/') {
        window.location.href = '/' + href;
        return;
      }
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setOpen(false);
  };

  const isActive = (href) => {
    if (href.startsWith('#')) return false;
    if (href === '/') return path === '/';
    return path.startsWith(href);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-md'
          : 'bg-white/95 backdrop-blur-xl border-b border-gray-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">

          {/* Logo */}
          <a href="/" data-testid="nav-logo" className="flex items-center gap-3 flex-shrink-0 group">
            <div className="w-9 h-9 rounded-full bg-[#1f3d2b] flex items-center justify-center flex-shrink-0 group-hover:bg-[#2d5a3e] transition-colors">
              <Leaf size={16} className="text-white" />
            </div>
            <div>
              <span className="block font-['Cormorant_Garamond'] text-xl sm:text-2xl font-semibold text-[#1f3d2b] leading-none">
                Healthy Life
              </span>
              <span className="block text-[9px] tracking-[0.28em] uppercase text-[#d97736] font-bold mt-0.5">
                Vitamins
              </span>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleClick(e, link.href)}
                className={`text-sm font-semibold tracking-wide transition-colors relative after:absolute after:bottom-[-3px] after:left-0 after:right-0 after:h-0.5 after:bg-[#1f3d2b] after:scale-x-0 after:transition-transform hover:after:scale-x-100 ${
                  isActive(link.href)
                    ? 'text-[#1f3d2b] after:scale-x-100'
                    : 'text-gray-600 hover:text-[#1f3d2b]'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:5103380667"
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#1f3d2b] transition-colors"
              data-testid="nav-phone"
            >
              <Phone size={14} />
              <span className="font-medium">(510) 338-0667</span>
            </a>
            <a
              href="/booking"
              data-testid="nav-book-cta"
              className="bg-[#1f3d2b] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#2d5a3e] transition-all hover:-translate-y-px active:translate-y-0 shadow-sm"
            >
              Book a Session
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-gray-700 hover:text-[#1f3d2b] transition-colors"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden bg-white border-t border-gray-100 shadow-lg animate-in slide-in-from-top-2 duration-200"
        >
          <nav className="max-w-7xl mx-auto px-4 py-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleClick(e, link.href)}
                className={`block py-3 text-base font-semibold border-b border-gray-50 last:border-0 transition-colors ${
                  isActive(link.href) ? 'text-[#1f3d2b]' : 'text-gray-700'
                }`}
              >
                {link.label}
              </a>
            ))}
            <div className="pt-4 pb-2 space-y-3">
              <a
                href="tel:5103380667"
                className="flex items-center gap-2 text-sm text-gray-600"
              >
                <Phone size={14} />
                (510) 338-0667
              </a>
              <a
                href="/booking"
                className="block w-full text-center bg-[#1f3d2b] text-white font-semibold py-3.5 rounded-full"
              >
                Book a Session
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
